/**
 * Chunk 7 — Same-invoice multi-page handling inside one upload session.
 *
 * After OCR, combine header fields onto the richest sibling page when files
 * look like pages of one invoice. Every uploaded image stays listed — empty /
 * sparse OCR is never discarded in the background; the user fills details or
 * removes each file themselves.
 */

import { normaliseInvoiceNumberForMultiDoc } from './invoiceMultiDocRules'

function normaliseSupplierKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
}

/** Stems too generic to treat trailing digits as page numbers (Invoice 123 vs Invoice 456). */
const GENERIC_FILENAME_STEMS = new Set([
  'invoice',
  'inv',
  'scan',
  'document',
  'doc',
  'file',
  'image',
  'img',
  'photo',
  'creditnote',
  'credit',
  'receipt',
])

function filenameStemKey(stem = '') {
  return String(stem || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
}

function filenameLooksLikePageSibling(a = '', b = '') {
  const left = String(a || '').toLowerCase()
  const right = String(b || '').toLowerCase()
  if (!left || !right || left === right) return false

  const stripPage = name =>
    name
      .replace(/\.(png|jpe?g|pdf)$/i, '')
      .replace(/[-_\s]?(?:p|page|pg)[-_\s]?\d+$/i, '')
      .replace(/[-_\s]?\d+$/i, '')

  const baseLeft = stripPage(left)
  const baseRight = stripPage(right)
  const stemKey = filenameStemKey(baseLeft)
  // Require a distinctive stem after stripping digits so "Invoice 4723714.pdf"
  // and "Invoice 4727383.pdf" are not treated as page siblings.
  if (
    baseLeft &&
    baseRight &&
    baseLeft === baseRight &&
    stemKey &&
    !GENERIC_FILENAME_STEMS.has(stemKey)
  ) {
    return true
  }

  return (
    /(?:p|page|pg)[-_\s]?[12]\b/i.test(left) &&
    /(?:p|page|pg)[-_\s]?[12]\b/i.test(right)
  )
}

/** How many mandatory-ish upload fields are filled (0–5). */
export function scoreUploadFileOcrRichness(file = {}) {
  const hasSupplier =
    (file.supplier != null && file.supplier !== '') ||
    Boolean(String(file.supplierTitle || '').trim())
  const hasPo = Boolean(String(file.purchaseOrderNumber || '').trim())
  const hasInvoiceNumber = Boolean(String(file.invoiceNumber || '').trim())
  const hasDate = Boolean(file.invoiceDate)
  const hasTotal = Boolean(String(file.invoiceTotalInput || '').trim())

  return [hasSupplier, hasPo, hasInvoiceNumber, hasDate, hasTotal].filter(
    Boolean,
  ).length
}

/**
 * OCR finished but almost nothing usable — show fill/remove guidance in review.
 * Unfinished reads (idle / pending / missing status) stay resumable in Incomplete.
 */
export function isSparseUploadOcrFile(file = {}) {
  if (file.ocrStatus === 'failed') return true
  if (file.ocrStatus !== 'suggested') return false
  return scoreUploadFileOcrRichness(file) < 2
}

/**
 * Score a library pending invoice using top-level fields + OCR suggestions.
 * Used to catch abandoned stage rows that never reached Confirm.
 */
export function scoreLibraryInvoiceOcrRichness(invoice = {}) {
  const suggested = invoice?.ocr?.suggested || {}
  const hasSupplier =
    (invoice.supplier != null && invoice.supplier !== '') ||
    Boolean(String(invoice.supplierTitle || '').trim()) ||
    Boolean(String(suggested.supplierName || '').trim())
  const hasPo =
    Boolean(String(invoice.purchaseOrderNumber || '').trim()) ||
    Boolean(String(suggested.poNumber || '').trim())
  const hasInvoiceNumber =
    Boolean(String(invoice.invoiceNumber || '').trim()) ||
    Boolean(String(suggested.invoiceNumber || '').trim())
  const hasDate = Boolean(invoice.invoiceDate || suggested.invoiceDate)
  const hasTotal =
    invoice.invoiceTotal != null ||
    suggested.grossTotal != null ||
    suggested.estimatedGrossTotalFromLines != null ||
    suggested.netTotal != null

  return [hasSupplier, hasPo, hasInvoiceNumber, hasDate, hasTotal].filter(
    Boolean,
  ).length
}

/**
 * True when a pending library invoice is blank / near-blank after OCR.
 * These stay listed for the user to fill or remove — never auto-discarded.
 * Only judges invoices that already ran OCR (suggested/failed) — raw stage
 * rows without OCR stay for resume / 7-day cleanup.
 */
export function isSparseLibraryIncompleteInvoice(invoice = {}) {
  if (!invoice) return false
  if (invoice.isArchived) return false
  const status = String(invoice.status || 'pending').trim()
  if (status && status !== 'pending') return false
  if (
    invoice.linkedOrderId ||
    (Array.isArray(invoice.matchedOrderIds) &&
      invoice.matchedOrderIds.length > 0)
  ) {
    return false
  }
  const ocrStatus = String(invoice.ocr?.status || '').trim()
  const hasOcrAttempt =
    ocrStatus === 'suggested' ||
    ocrStatus === 'failed' ||
    Boolean(invoice.ocr?.suggested)
  if (!hasOcrAttempt) return false
  if (ocrStatus === 'failed') return true
  return scoreLibraryInvoiceOcrRichness(invoice) < 2
}

/**
 * Same-invoice field merge for Incomplete / Review later.
 * Never returns discard ids — sparse stubs stay for the user to review.
 */
export function collectSparseSessionDiscardInvoiceIds({
  drafts = [],
  files = [],
} = {}) {
  const merged = mergeSameInvoiceSessionPages({ drafts, files })
  return {
    drafts: merged.drafts,
    files: merged.files,
    discardInvoiceIds: [],
    mergeBanner: merged.mergeBanner,
  }
}

function mergeUploadFileFields(primary = {}, secondary = {}) {
  const next = { ...primary }
  const touched = new Set(primary.userTouchedFields || [])

  if (
    !touched.has('supplierTitle') &&
    !String(next.supplierTitle || '').trim() &&
    secondary.supplierTitle
  ) {
    next.supplierTitle = secondary.supplierTitle
  }
  if (!touched.has('supplier') && !next.supplier && secondary.supplier) {
    next.supplier = secondary.supplier
  }
  if (
    !touched.has('purchaseOrderNumber') &&
    !String(next.purchaseOrderNumber || '').trim() &&
    secondary.purchaseOrderNumber
  ) {
    next.purchaseOrderNumber = secondary.purchaseOrderNumber
    if (secondary.linkedOrderId) next.linkedOrderId = secondary.linkedOrderId
  }
  if (!String(next.invoiceNumber || '').trim() && secondary.invoiceNumber) {
    next.invoiceNumber = secondary.invoiceNumber
  }
  if (!next.invoiceDate && secondary.invoiceDate) {
    next.invoiceDate = secondary.invoiceDate
  }
  if (
    !String(next.invoiceTotalInput || '').trim() &&
    secondary.invoiceTotalInput
  ) {
    next.invoiceTotalInput = secondary.invoiceTotalInput
    next.invoiceTotalSource =
      secondary.invoiceTotalSource || next.invoiceTotalSource
  }
  if (
    next.documentType === 'invoice' &&
    secondary.documentType &&
    secondary.documentType !== 'invoice'
  ) {
    next.documentType = secondary.documentType
  }

  const primarySuggested = primary.ocrSuggested || {}
  const secondarySuggested = secondary.ocrSuggested || {}
  next.ocrSuggested = {
    ...secondarySuggested,
    ...primarySuggested,
    supplierName:
      primarySuggested.supplierName || secondarySuggested.supplierName || null,
    poNumber: primarySuggested.poNumber || secondarySuggested.poNumber || null,
    invoiceNumber:
      primarySuggested.invoiceNumber ||
      secondarySuggested.invoiceNumber ||
      null,
    referencedInvoiceNumber:
      primarySuggested.referencedInvoiceNumber ||
      secondarySuggested.referencedInvoiceNumber ||
      null,
    invoiceDate:
      primarySuggested.invoiceDate || secondarySuggested.invoiceDate || null,
    netTotal:
      primarySuggested.netTotal != null
        ? primarySuggested.netTotal
        : secondarySuggested.netTotal ?? null,
    vatTotal:
      primarySuggested.vatTotal != null
        ? primarySuggested.vatTotal
        : secondarySuggested.vatTotal ?? null,
    grossTotal:
      primarySuggested.grossTotal != null
        ? primarySuggested.grossTotal
        : secondarySuggested.grossTotal ?? null,
    estimatedGrossTotalFromLines:
      primarySuggested.estimatedGrossTotalFromLines != null
        ? primarySuggested.estimatedGrossTotalFromLines
        : secondarySuggested.estimatedGrossTotalFromLines ?? null,
    documentTypeGuess:
      primarySuggested.documentTypeGuess ||
      secondarySuggested.documentTypeGuess ||
      null,
  }

  const warn = [
    ...(Array.isArray(primary.ocrWarnings) ? primary.ocrWarnings : []),
    ...(Array.isArray(secondary.ocrWarnings) ? secondary.ocrWarnings : []),
    'merged_same_invoice_pages',
  ]
  next.ocrWarnings = [...new Set(warn)]

  return next
}

function filesLookLikeSameInvoice(a = {}, b = {}) {
  const invA = normaliseInvoiceNumberForMultiDoc(a.invoiceNumber)
  const invB = normaliseInvoiceNumberForMultiDoc(b.invoiceNumber)
  // Two different invoice numbers in one pick are two documents, not pages.
  // Same-supplier + adjacent used to swallow the second Oliver Kay (etc).
  if (invA && invB) return invA === invB

  if (filenameLooksLikePageSibling(a.filename, b.filename)) return true

  const supplierA = normaliseSupplierKey(a.supplierTitle)
  const supplierB = normaliseSupplierKey(b.supplierTitle)
  const pickA = Number(a.pickIndex)
  const pickB = Number(b.pickIndex)
  const adjacent =
    Number.isFinite(pickA) &&
    Number.isFinite(pickB) &&
    Math.abs(pickA - pickB) === 1

  if (supplierA && supplierA === supplierB && adjacent) {
    // One rich page + one sparse sibling of the same supplier in the same pick
    const richA = scoreUploadFileOcrRichness(a)
    const richB = scoreUploadFileOcrRichness(b)
    if (richA >= 2 || richB >= 2) return true
    // Both sparse but same supplier + adjacent — group for field merge only
    if (richA < 2 && richB < 2) return true
  }

  return false
}

/**
 * Cluster files in pick order into same-invoice page groups.
 */
export function groupSameInvoiceUploadPages(files = []) {
  const list = [...(files || [])].sort(
    (a, b) => Number(a.pickIndex ?? 0) - Number(b.pickIndex ?? 0),
  )
  const groups = []
  const used = new Set()

  for (let i = 0; i < list.length; i += 1) {
    const file = list[i]
    const id = String(file.draftId || file.invoiceId || i)
    if (used.has(id)) continue

    const group = [file]
    used.add(id)

    for (let j = i + 1; j < list.length; j += 1) {
      const other = list[j]
      const otherId = String(other.draftId || other.invoiceId || j)
      if (used.has(otherId)) continue
      if (group.some(member => filesLookLikeSameInvoice(member, other))) {
        group.push(other)
        used.add(otherId)
      }
    }

    groups.push(group)
  }

  return groups
}

/**
 * Merge OCR fields onto the richest same-invoice page.
 * Keeps every uploaded file visible — never discards siblings in the background.
 *
 * @returns {{
 *   drafts: object[],
 *   files: object[],
 *   discardInvoiceIds: string[],
 *   mergeBanner: string|null,
 * }}
 */
export function mergeSameInvoiceSessionPages({ drafts = [], files = [] } = {}) {
  const groups = groupSameInvoiceUploadPages(files)
  let mergedPageCount = 0
  let nextFiles = [...(files || [])]
  const nextDrafts = [...(drafts || [])]

  for (const group of groups) {
    if (group.length < 2) {
      // Lone file (including sparse OCR) — keep for manual fill / remove.
      continue
    }

    const ranked = [...group].sort(
      (a, b) => scoreUploadFileOcrRichness(b) - scoreUploadFileOcrRichness(a),
    )
    let primary = ranked[0]
    const siblings = ranked.slice(1)

    for (const sibling of siblings) {
      primary = mergeUploadFileFields(primary, sibling)
    }

    mergedPageCount += group.length
    nextFiles = nextFiles.map(file =>
      String(file.draftId) === String(primary.draftId) ? primary : file,
    )
    // Siblings stay listed so the user can fill details or remove any image.
  }

  const mergeBanner =
    mergedPageCount > 1
      ? `These look like ${mergedPageCount} pages of the same invoice — each file is still listed so you can fill details or remove any you don’t need.`
      : null

  return {
    drafts: nextDrafts,
    files: nextFiles,
    discardInvoiceIds: [],
    mergeBanner,
  }
}
